import { useCallback, useEffect, useRef, useState } from "react";

import { Link, useLocation, useNavigate } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";

import { logoutFetch } from "../../../store/authSlice";

import { getLoginPath } from "../../../utils/authRedirect";

import { NAV_ICONS, IconBell, IconSettings } from "../../ui/icons";

import clinicAPI from "../../../services/clinicApi";

import { countPendingPolls } from "../../../utils/clientSessionUtils";

import { givenNameFromFullName } from "../../../utils/clientAppUtils";

import { useClientAthlete } from "../../../context/ClientAthleteContext";

import ClientChildSwitcher from "./ClientChildSwitcher";

import ClientSplash from "./ClientSplash";

import ClientPushBanner from "./ClientPushBanner";

import DesktopSidebar, { desktopSidebarStyles as ds } from "../common/DesktopSidebar";

import NotificationsDropdown from "../common/NotificationsDropdown";

import useClubPortalBranding from "../../../hooks/useClubPortalBranding";

import { usePwaChrome, CLIENT_PAGE_BG } from "../../../utils/pwaThemeColor";

import {

  usePullToRefresh,

  PullToRefreshIndicator,

} from "../../../hooks/usePullToRefresh";

import { dispatchClientRefresh } from "../../../hooks/useClientRefresh";

import { useIsMobile } from "../../../hooks/useMediaQuery";
import { usePortalHtmlClass } from "../../../hooks/usePortalHtmlClass";

import { useSkipPwaSplash, useShowPwaBanners } from "../../../hooks/useStandalone";

import {

  isPushSupported,

  registerServiceWorker,

  syncPushSubscriptionIfGranted,

} from "../../../utils/pushNotifications";

import L from "../../../constants/baysportLabels";

import desk from "./clientDesktop.module.css";

import s from "./clientApp.module.css";



const NAV = [

  { id: "home", to: "/app", end: true, label: "Главная", icon: "overview" },

  { id: "calendar", to: "/app/calendar", label: "Календарь", icon: "schedule" },

  { id: "chat", to: "/app/chat", label: "Чат", icon: "chat" },

  { id: "life", to: "/app/life", label: "Клуб", icon: "venues" },

  { id: "profile", to: "/app/profile", label: "Профиль", icon: "clients" },

];



const PAGE_TITLES = {

  "/app": null,

  "/app/calendar": "Календарь",

  "/app/chat": "Сообщения",

  "/app/life": "Клуб",

  "/app/profile": "Профиль",

  "/app/stats": "Рейтинг",

  "/app/notifications": "Уведомления",

  "/app/members": "Сообщество",

};



const SELF_HEADER = [

  "/app",

  "/app/calendar",

  "/app/life",

  "/app/profile",

  "/app/stats",

  "/app/chat",

];



function todayLabel() {

  return new Date().toLocaleDateString("ru-RU", {

    weekday: "long",

    day: "numeric",

    month: "long",

  });

}



function isSelfHeaderPath(base) {

  return SELF_HEADER.some(

    (path) => base === path || base.startsWith(`${path}/`),

  );

}



function resolvePageTitle(base) {

  if (PAGE_TITLES[base]) return PAGE_TITLES[base];

  if (base.startsWith("/app/profile/")) return "Профиль";

  if (base.startsWith("/app/members/")) return "Профиль участника";

  if (base.startsWith("/app/chat/")) return "Сообщения";

  return null;

}



export default function ClientAppShell({ children }) {

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { pathname } = useLocation();

  const { tenant, profile } = useSelector((state) => state.auth);

  const {

    athleteQueryParam,

    activeAthleteId,

    activeAthlete,

    isParentMode,

    athletes,

  } = useClientAthlete();

  const [pollBadge, setPollBadge] = useState(0);

  const [chatBadge, setChatBadge] = useState(0);

  const [notifyUnread, setNotifyUnread] = useState(0);

  const [notifyOpen, setNotifyOpen] = useState(false);

  const notifyBtnRef = useRef(null);

  const skipSplash = useSkipPwaSplash();

  const showPwaBanners = useShowPwaBanners();

  const isMobile = useIsMobile(768);

  const [splashDone, setSplashDone] = useState(skipSplash);

  const [swReady, setSwReady] = useState(false);

  const splashForcedRef = useRef(false);



  const handleSplashComplete = useCallback(() => {

    splashForcedRef.current = true;

    setSplashDone(true);

  }, []);



  useEffect(() => {

    const safety = window.setTimeout(() => {

      if (!splashForcedRef.current) setSplashDone(true);

    }, 5000);

    return () => window.clearTimeout(safety);

  }, []);



  const loadBadge = useCallback(async () => {

    try {

      const [polls, chatUnread, notif] = await Promise.all([

        clinicAPI.getMyTrainingPolls(athleteQueryParam).catch(() => ({ polls: [] })),

        clinicAPI

          .getMyChatUnread(athleteQueryParam)

          .catch(() => ({ unread_count: 0 })),

        clinicAPI

          .getMyNotifications(athleteQueryParam)

          .catch(() => ({ unread_count: 0 })),

      ]);

      setPollBadge(countPendingPolls(polls.polls));

      setChatBadge(chatUnread.unread_count || 0);

      setNotifyUnread(notif.unread_count || 0);

    } catch {

      setPollBadge(0);

      setChatBadge(0);

      setNotifyUnread(0);

    }

  }, [athleteQueryParam]);



  useEffect(() => {

    loadBadge();

    const id = setInterval(loadBadge, 30_000);

    const onActivity = () => loadBadge();

    window.addEventListener("baysport:chat-activity", onActivity);

    window.addEventListener("baysport:poll-activity", onActivity);

    return () => {

      clearInterval(id);

      window.removeEventListener("baysport:chat-activity", onActivity);

      window.removeEventListener("baysport:poll-activity", onActivity);

    };

  }, [loadBadge, pathname, activeAthleteId]);



  useEffect(() => {

    const onVis = () => {

      if (document.visibilityState === "visible") {

        dispatchClientRefresh();

      }

    };

    document.addEventListener("visibilitychange", onVis);

    return () => document.removeEventListener("visibilitychange", onVis);

  }, []);



  useEffect(() => {

    if (!splashDone) return undefined;

    const timer = window.setTimeout(() => {

      registerServiceWorker().then(() => {

        setSwReady(true);

        if (isPushSupported()) syncPushSubscriptionIfGranted();

      });

    }, 1500);

    return () => window.clearTimeout(timer);

  }, [splashDone]);



  usePortalHtmlClass("client");



  const base = pathname.replace(/\/$/, "") || "/app";

  const selfHeader = isSelfHeaderPath(base);

  const isHome = base === "/app";

  const isCalendar = base === "/app/calendar";

  const isChat = base === "/app/chat" || base.startsWith("/app/chat/");

  const pageTitle = resolvePageTitle(base);

  const showShellChildBar = !isHome && isParentMode && athletes.length > 1;

  const notifyBadgeCount = pollBadge + notifyUnread;



  const firstName = givenNameFromFullName(

    isParentMode && activeAthlete ? activeAthlete.full_name : profile?.full_name,

  );



  const { shellStyle, theme } = useClubPortalBranding("ca", null, false, splashDone);



  const immersiveCover =

    base === "/app" || base === "/app/life" || base === "/app/profile";

  usePwaChrome(immersiveCover ? theme?.from : CLIENT_PAGE_BG, {

    enabled: splashDone && Boolean(theme) && isMobile,

    isDark: immersiveCover,

    immersive: immersiveCover,

  });



  const handlePullRefresh = useCallback(async () => {

    dispatchClientRefresh();

    await new Promise((r) => setTimeout(r, 400));

  }, []);



  const { pulling, pullDistance } = usePullToRefresh(handlePullRefresh, {

    enabled: splashDone && isMobile,

  });



  const logout = () => {

    dispatch(logoutFetch());

    navigate(getLoginPath("client"));

  };



  const navBadge = (item) => {

    if (item.id === "calendar") return pollBadge;

    if (item.id === "chat") return chatBadge;

    return 0;

  };



  const pageContent = (

    <div key={`${pathname}-${activeAthleteId || "self"}`} className={s.pageEnter}>

      {children}

    </div>

  );



  const desktopTopActions = (
    <>
      {showShellChildBar ? <ClientChildSwitcher compact /> : null}
      <button
        ref={notifyBtnRef}
        type="button"
        className={ds.iconBtn}
        title="Уведомления"
        aria-label="Уведомления"
        aria-expanded={notifyOpen}
        onClick={() => setNotifyOpen((v) => !v)}
      >
        <IconBell width={17} height={17} />
        {notifyBadgeCount > 0 ? (
          <span className={ds.navBadge}>{notifyBadgeCount > 9 ? "9+" : notifyBadgeCount}</span>
        ) : null}
      </button>
      <Link to="/app/profile/account" className={ds.iconBtn} title="Настройки">
        <IconSettings width={17} height={17} />
      </Link>
    </>
  );

  if (!isMobile && splashDone) {
    const flush = isChat || isCalendar;
    const showPageHead = isHome || pageTitle || showShellChildBar;

    return (
      <div className={`${ds.desktopRoot} ${desk.desktopRoot}`} style={shellStyle}>
        <DesktopSidebar
          homeTo="/app"
          brandName={tenant?.name || L.club}
          sections={[{ items: NAV }]}
          profile={profile}
          profileTo="/app/profile"
          onLogout={logout}
          getBadge={navBadge}
          headerActions={desktopTopActions}
        />

        <main className={ds.main}>
          {showPageHead ? (
            <div className={ds.pageHead}>
              <div className={ds.pageHeadRow}>
                {isHome ? (
                  <div>
                    <h1 className={ds.topBarTitle}>
                      {firstName ? `Привет, ${firstName}` : "Добро пожаловать"}
                    </h1>
                    <p className={ds.topBarMeta}>
                      {todayLabel()}
                      {tenant?.name ? ` · ${tenant.name}` : ""}
                    </p>
                  </div>
                ) : pageTitle ? (
                  <div>
                    <h1 className={ds.topBarTitle}>{pageTitle}</h1>
                    {isParentMode && athletes.length > 1 && !showShellChildBar ? (
                      <p className={ds.topBarMeta}>Выберите спортсмена в шапке</p>
                    ) : null}
                  </div>
                ) : (
                  <div />
                )}
              </div>
            </div>
          ) : null}

          <div
            className={isCalendar ? ds.contentSchedule : ds.mainScroll}
            data-pull-scroll={!isCalendar ? true : undefined}
          >
            <div className={flush ? ds.contentFlush : ds.content}>
              <div className={flush ? desk.desktopWide : desk.desktopMainInner}>{pageContent}</div>
            </div>
          </div>
        </main>

        <NotificationsDropdown
          open={notifyOpen}
          onClose={() => setNotifyOpen(false)}
          anchorRef={notifyBtnRef}
          allPath="/app/notifications"
          portal="client"
          athleteId={activeAthlete?.id || ""}
          onMarkedRead={() => {
            setNotifyUnread(0);
            loadBadge();
          }}
        />
      </div>
    );
  }



  return (

    <>

      {!splashDone ? (

        <ClientSplash clubName={tenant?.name} onComplete={handleSplashComplete} />

      ) : null}



      <div className={s.shell} style={splashDone ? shellStyle : undefined}>

        <PullToRefreshIndicator pulling={pulling} pullDistance={pullDistance} />

        {splashDone && swReady && showPwaBanners ? <ClientPushBanner /> : null}

        <main className={selfHeader ? s.mainHome : s.main} data-pull-scroll>

          {selfHeader && showShellChildBar ? (

            <div className={s.shellChildBar}>

              <ClientChildSwitcher compact />

            </div>

          ) : null}

          {pageContent}

        </main>



        <nav

          className={`${s.tabBar} ${splashDone ? "" : s.tabBarHidden}`}

          aria-label="Навигация"

        >

          {NAV.map((item) => {

            const Icon = NAV_ICONS[item.icon] || NAV_ICONS.overview;

            const active = item.end

              ? base === item.to

              : base === item.to || base.startsWith(`${item.to}/`);

            const badge = navBadge(item);

            return (

              <Link

                key={item.to}

                to={item.to}

                end={item.end}

                className={active ? `${s.tab} ${s.tabActive}` : s.tab}

                aria-current={active ? "page" : undefined}

              >

                <span className={s.tabIconWrap}>

                  <Icon className={s.tabIcon} />

                  {badge > 0 ? (

                    <span className={s.tabBadge}>{badge > 9 ? "9+" : badge}</span>

                  ) : null}

                </span>

                <span className={s.tabLabel}>{item.label}</span>

              </Link>

            );

          })}

        </nav>

      </div>

    </>

  );

}


